import {
  Box,
  Card,
  Typography,
  Modal,
  IconButton,
  useMediaQuery,
  Grid,
  CardMedia,
  CardContent,
  useTheme,
} from '@mui/material';

import { EmojiObjects, AddCircle, Repeat } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';

import add from '../app/assets/images/add.png';
import study from '../app/assets/images/study.png';
import recall from '../app/assets/images/recall.png';
import recall1 from '../app/assets/images/recall1.jpg';
import mastery from '../app/assets/images/mastery.png';

const UsageGuide = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const theme = useTheme(); 
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      imageUrl: study,
      icon: (
        <Box sx={{ color: theme.palette.warning.main }}>
          <EmojiObjects />
        </Box>
      ),
      title: 'Study Topic',
      description: (
        <>
          Study the topic until you achieve <strong>complete memorization</strong>.
        </>
      ),
    },
    {
      imageUrl: add,
      icon: (
        <Box sx={{ color: theme.palette.success.main }}>
          <AddCircle />
        </Box>
      ),
      title: 'Add to App',
      description: (
        <>
          Incorporate the topic into the app to <strong>monitor your progress</strong>.
        </>
      ),
    },
    {
      imageUrl: recall,
      icon: (
        <Box sx={{ color: theme.palette.info.main }}>
          <Repeat />
        </Box>
      ),
      title: 'Initial Revision',
      description: (
        <Box>
          <Typography><strong>1.</strong> When your memory shows <strong>40%</strong>, try to recall what you learned last.</Typography>
          <Typography><strong>2.</strong> Review the topic until you can <strong>remember everything</strong>.</Typography>
          <Typography><strong>3.</strong> <strong>Update the topic</strong> in the app once fully memorized.</Typography>
        </Box>
      ),
    },
    {
      imageUrl: recall1,
      icon: (
        <Box sx={{ color: theme.palette.info.main }}>
          <Repeat />
        </Box>
      ),
      title: 'Subsequent Revision',
      description: (
        <Box>
          <Typography><strong>1.</strong> After some time, check if your memory drops back to <strong>40%</strong>.</Typography>
          <Typography><strong>2.</strong> Review the material again until you reach <strong>complete memorization</strong>.</Typography>
          <Typography><strong>3.</strong> <strong>Update the topic</strong> in the app once mastered.</Typography>
        </Box>
      ),
    },
    {
      imageUrl: mastery,
      icon: (
        <Box sx={{ color: theme.palette.info.main }}>
          <Repeat />
        </Box>
      ),
      title: 'Reinforce Learning',
      description: (
        <Box>
          <Typography><strong>1.</strong> Continue to <strong>repeat the revision process regularly</strong>.</Typography>
          <Typography><strong>2.</strong> This will help <strong>reinforce your understanding</strong> and retention.</Typography>
        </Box>
      ),
    },
  ];

  const handleOpen = (index) => setOpenIndex(index);
  const handleClose = () => setOpenIndex(null);

  return (
    <Box sx={{ px: isMobile ? 2 : 4, py: 6, maxWidth: 1200, mx: 'auto' }}>
    <Typography
      variant={isMobile ? 'h5' : 'h4'}
      fontWeight={700}
      textAlign="center"
      gutterBottom
      sx={{ color: 'text.primary', letterSpacing: 0.5 }}
    >
      How to Use This App
    </Typography>
    <Typography
      variant="body1"
      color="text.secondary"
      textAlign="center"
      mb={4}
      sx={{ fontStyle: 'italic' }}
    >
      Follow these simple steps to get started.
    </Typography>
  
    <Grid container spacing={4}>
      {steps.map((step, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: 6,
              cursor: 'pointer',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: 10,
              },
              backgroundColor: 'white',
              overflow: 'hidden',
            }}
            onClick={() => handleOpen(index)}
          >
            <CardMedia
              component="img"
              image={step.imageUrl}
              alt={step.title}
              height="180"
              sx={{
                objectFit: 'cover',
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
                boxShadow: 'inset 0 0 15px rgba(0, 0, 0, 0.1)', // subtle inset shadow to lift the image
              }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {step.icon}
                <Typography variant="h6" fontWeight={600} sx={{ color: 'primary.main' }}>
                  {step.title}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'justify' }}>
                {step.description}
              </Typography>
            </CardContent>
          </Card>
  
          {/* Modal */}
          <Modal open={openIndex === index} onClose={handleClose}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: 24,
                p: isMobile ? 2 : 3,
                maxWidth: '90vw',
                maxHeight: '90vh',
                outline: 'none',
                overflow: 'auto',
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'grey.700',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={step.imageUrl}
                alt="Step Detail"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  display: 'block',
                  mx: 'auto',
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />
            </Box>
          </Modal>
        </Grid>
      ))}
    </Grid>
  </Box>
  
  
  );
};

export default UsageGuide;
